// Npm
import { Button } from '@nt/dds-react';
import moment from 'moment';
import React, { FC, ReactElement, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

// Local
import { useApAsyncDispatch, useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { Constants } from '../../../../constants/create';
import {
  fetchCoOwnershipList,
  selectPrefillData,
  setCreateDraftData,
  setEditMode
} from '../../../../pages/Create/CreateSlice';
import {
  selectFeatureFlags,
  setAnalyticsData,
  setNavigationFromDashboard,
  setUserAddressDetails,
  setUserLoggedOut
} from '../../../../pages/Home/HomeSlice';
import { setEntityDraftData } from '../../../../pages/LegalEntity/LegalEntitySlice';
import { axiosApiCall } from '../../../../services/apiService';
import { checkPartyIsExternal, getCownershipPayload, getLegalEntityCategory } from '../../../../services/common';
import { getDataFromDraft } from '../../../../services/createDataFromDraft';
import { Feature, isFeatureEnabled } from '../../../../utils/featureFlag';
import { uniqueID } from '../../../../utils/helper';
import {
  ApiResponse,
  ClubbedEntityDetails,
  DraftContentData,
  DraftDetails,
  ItemsInprogressGridData,
  RulesValue
} from '../../../../utils/types';

interface Props {
  data: ItemsInprogressGridData;
}

/**
 * @param {*} {data: ItemsInprogressGridData}
 * @return {ReactElement}
 */
const LinkButtonCellRenderer: FC<Props> = ({ data }: Props): ReactElement => {
  const storeAsyncDispatch = useApAsyncDispatch();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { referenceData } = useAppSelector(selectPrefillData);
  const featureFlags = useAppSelector(selectFeatureFlags);

  const setCoOwnershipList = useCallback(
    async (entityDetails: ClubbedEntityDetails, draftStage: string) => {
      const requestPayload = getCownershipPayload(entityDetails);
      if (entityDetails?.maritalStatus && entityDetails?.maritalStatus !== '') {
        await storeAsyncDispatch(fetchCoOwnershipList(requestPayload));
      }
      history.push(`/create/${draftStage}`);
    },
    [history, storeAsyncDispatch]
  );

  const onClickHandler = useCallback(
    async (draftId: string) => {
      try {
        dispatch(setUserLoggedOut(false));
        const rulesUrl: string = process.env.UXP_DRAFT_URL as string;
        const responseData: ApiResponse = await axiosApiCall('GET', `${rulesUrl}/draft/${draftId}`);
        if (responseData?.data?.resultSet) {
          const [draftContentData] = responseData.data.resultSet as DraftContentData[];
          const {
            draftId,
            createdDate,
            draftStatus,
            lastModifiedByDate,
            draftStage,
            draftContent,
            leDraftUUID,
            accountDraftUUID,
            lgDraftUUID,
            ultimateParentUlek,
            analyticsData
          } = draftContentData;
          const draftStepIndex = Constants.createSteps.findIndex(step => step.route === draftStage);
          const beneficialOwnerCheck = draftStepIndex > 1;
          const isExternalDraft = checkPartyIsExternal(
            draftContent?.externalPartySource as string,
            draftContent?.externalPartyUlek as string
          );
          const resumedIsEditMode = isExternalDraft && isFeatureEnabled(featureFlags, Feature.EDIT);
          const { accountOpenTimestamp, draftRandomId } = analyticsData || {};
          dispatch(
            setAnalyticsData({
              accountOpenTimestamp: accountOpenTimestamp ?? moment().format('YYYY-MM-DD HH:mm:ss'),
              draftRandomId: draftRandomId ?? uniqueID()
            })
          );
          dispatch(setEditMode(resumedIsEditMode));
          dispatch(
            setEntityDraftData({
              legalEntityCategory: getLegalEntityCategory(draftContent.legalEntityCategory as string),
              accountType: 'Custody Account'
            })
          );
          const {
            entityDetails,
            officers,
            controlPerson,
            corporateSecretary,
            beneficialOwners,
            authorizedAgents,
            coOwners,
            accountDetails,
            featuresSetDefault,
            cashSweepOptions,
            reportingParameters,
            taxParameters,
            servicesSetDefault,
            reportingAnalytics,
            dataFeed,
            fundingData,
            signerPartyDetails,
            documentFolderDetails,
            clientUploadedDocuments,
            fiduciary,
            powerHoldingRole,
            isMaintenanceRequired
          } = getDataFromDraft(draftContent, referenceData);

          const draftDetails: DraftDetails = {
            draftId,
            draftStage,
            draftStatus,
            createdDate,
            lastModifiedByDate,
            leDraftUUID,
            accountDraftUUID,
            lgDraftUUID,
            ultimateParentUlek
          };

          dispatch(setUserAddressDetails([...(entityDetails?.address || [])]));

          dispatch(
            setCreateDraftData({
              draftDetails,
              entityDetails,
              officers,
              controlPerson,
              corporateSecretary,
              beneficialOwners,
              beneficialOwnerCheck,
              authorizedAgents,
              coOwners,
              accountDetails,
              featuresSetDefault,
              cashSweepOptions,
              reportingParameters,
              taxParameters,
              servicesSetDefault,
              reportingAnalytics,
              dataFeed,
              fundingData,
              signerPartyDetails,
              documentFolderDetails,
              clientUploadedDocuments,
              fiduciary,
              powerHoldingRole,
              isMaintenanceRequired
            })
          );
          if (
            getLegalEntityCategory(draftContent.legalEntityCategory as string).toUpperCase() as RulesValue ===
              RulesValue.INDIVIDUAL &&
            !isExternalDraft
          ) {
            dispatch(setNavigationFromDashboard(true));
            setCoOwnershipList(entityDetails as ClubbedEntityDetails, draftStage);
          } else {
            history.push(`/create/${draftStage}`);
          }
        }
      } catch {
        return;
      }
    },
    [dispatch, featureFlags, history, referenceData, setCoOwnershipList]
  );

  return (
    <>
      {data && data.viewDetails && (
        <div className="status-button">
          <Button
            icon="none"
            iconName="none"
            kind="borderless"
            onClick={() => onClickHandler(data.draftId)}
            size="small"
            type="button"
          >
            {'View Details'}
          </Button>
        </div>
      )}
    </>
  );
};

LinkButtonCellRenderer.displayName = 'LinkButtonCellRenderer';
export default LinkButtonCellRenderer;
